import { IconCopy, IconWebhook } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { WebhookHealth, WebhookRow } from '../routes/AccountWebhooks';
import { ConfirmDialog } from './ConfirmDialog';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// One account webhook as a row-list row (spec §3.15): icon tile + stacked
// endpoint/events/success-rate lines, ghost action right. Presenter.
//
// DELETE ASKS FIRST (plan 101 slice 17 row 17-7): the endpoint, its secret and
// its delivery history go with it, and it sat one click away beside Send test.
// The confirm is a rendering concern, so it lives with the button (the
// `BlockerWithdrawButton` reasoning); `onDelete` fires only from the dialog.

export type WebhookCardProps = {
  readonly row: WebhookRow;
  /**
   * Fires the test-delivery endpoint for this webhook. Optional in the
   * scaffold — when omitted, the "Send test" button renders disabled so the
   * UI doesn't advertise an action the caller hasn't wired.
   */
  readonly onSendTest?: (id: string) => void;
  /** Deletes this webhook, once confirmed. Omitted → the delete button is not rendered. */
  readonly onDelete?: (id: string) => void;
  /** A write on THIS webhook is in flight: both actions hold until it settles. */
  readonly busy?: boolean;
};

const HEALTH: Record<WebhookHealth, { readonly key: string; readonly tone: StatusTone }> = {
  healthy: { key: 'account_webhooks.health_healthy', tone: 'success' },
  retrying: { key: 'account_webhooks.health_retrying', tone: 'warning' },
  unhealthy: { key: 'account_webhooks.health_unhealthy', tone: 'danger' },
};

export function WebhookCard(props: WebhookCardProps): JSX.Element {
  const handleSendTest = (): void => props.onSendTest?.(props.row.id);
  const [asking, setAsking] = createSignal(false);
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copyUrl = (): void => void navigator.clipboard?.writeText(props.row.url_masked);

  return (
    <li class="row-list__row webhook-entry">
      <div class="row-list__lead webhook-entry__lead">
        <span class="row-list__tile">
          <IconWebhook />
        </span>
        <div class="webhook-entry__lines">
          <p class="webhook-entry__name">
            {props.row.name}
            <StatusPill tone={HEALTH[props.row.health].tone}>
              {t(HEALTH[props.row.health].key)}
            </StatusPill>
          </p>
          <p class="webhook-entry__url">
            <code>{props.row.url_masked}</code>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('account_webhooks.copy_url')}
              onClick={copyUrl}
            >
              <IconCopy size="0.875rem" />
            </Button>
          </p>
          <ul class="webhook-entry__tags">
            <For each={props.row.events}>
              {(event) => (
                <li>
                  <span class="badge badge--outline">{event}</span>
                </li>
              )}
            </For>
          </ul>
          <p class="webhook-entry__meta">
            {/* No resolved delivery backs a rate — the ledger read has not
                landed, or this endpoint has never fired. Says so, rather than
                printing the 100% the absent case used to default to. */}
            <span>
              <Show
                when={props.row.success_pct !== null}
                fallback={t('account_webhooks.success_rate_unknown')}
              >
                {t('account_webhooks.success_rate', { pct: props.row.success_pct ?? 0 })}
              </Show>
            </span>
            <span>
              <Show
                when={props.row.retrying}
                fallback={t('account_webhooks.last_delivery', { when: props.row.last_delivery })}
              >
                {(count) => tPlural('account_webhooks.retrying_note', count(), { count: count() })}
              </Show>
            </span>
          </p>
        </div>
      </div>
      <div class="webhook-entry__actions">
        <Button
          variant="ghost"
          size="sm"
          disabled={!props.onSendTest || props.busy === true}
          onClick={handleSendTest}
        >
          {t('account_webhooks.send_test')}
        </Button>
        <Show when={props.onDelete}>
          <button
            type="button"
            class={`${buttonClass({ variant: 'ghost', size: 'sm' })} webhook-entry__delete`}
            disabled={props.busy === true}
            onClick={() => setAsking(true)}
          >
            {t('account_webhooks.delete')}
          </button>
          <ConfirmDialog
            open={asking()}
            title={t('account_webhooks.delete_title', { name: props.row.name })}
            desc={t('account_webhooks.delete_desc')}
            cancelLabel={t('account_webhooks.delete_cancel')}
            actionLabel={t('account_webhooks.delete_confirm')}
            destructive
            onCancel={() => setAsking(false)}
            onConfirm={() => {
              setAsking(false);
              props.onDelete?.(props.row.id);
            }}
          />
        </Show>
      </div>
    </li>
  );
}
